import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import {
  GoogleAuthProvider,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { env } from '../../lib/env';
import { getFirebaseAuth } from '../../lib/firebase';

export interface PortalUser {
  uid: string;
  email: string;
  displayName: string;
  isAdmin: boolean;
}

export interface AuthContextValue {
  user: PortalUser | null;
  loading: boolean;
  error: string | null;
  signInWithPortalToken: (customToken: string) => Promise<PortalUser>;
  signInAdminWithEmail: (email: string, password: string) => Promise<PortalUser>;
  signInAdminWithGoogle: () => Promise<PortalUser>;
  refreshClaims: () => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

function mockUser(): PortalUser | null {
  if (env.mockRole === 'anonymous') return null;
  if (env.mockRole === 'admin') {
    return {
      uid: 'admin_demo',
      email: 'operasyon@tropikalakdeniz.com',
      displayName: 'Tropikal Operasyon',
      isAdmin: true,
    };
  }
  return {
    uid: 'shopify_7845129630',
    email: 'deniz@example.com',
    displayName: 'Deniz Yılmaz',
    isAdmin: false,
  };
}

async function toPortalUser(firebaseUser: User, forceRefresh = false): Promise<PortalUser> {
  const token = await firebaseUser.getIdTokenResult(forceRefresh);
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email ?? '',
    displayName: firebaseUser.displayName ?? firebaseUser.email?.split('@')[0] ?? 'Müşteri',
    isAdmin: token.claims.admin === true,
  };
}

async function requireAdminUser(firebaseUser: User): Promise<PortalUser> {
  const portalUser = await toPortalUser(firebaseUser, true);
  if (!portalUser.isAdmin) {
    await firebaseSignOut(getFirebaseAuth());
    throw new Error('Bu hesap yönetici yetkisine sahip değil.');
  }
  return portalUser;
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<PortalUser | null>(() => (env.useMockApi ? mockUser() : null));
  const [loading, setLoading] = useState(!env.useMockApi);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (env.useMockApi) return;
    try {
      const unsubscribe = onIdTokenChanged(
        getFirebaseAuth(),
        (firebaseUser) => {
          if (!firebaseUser) {
            setUser(null);
            setLoading(false);
            return;
          }
          void toPortalUser(firebaseUser)
            .then((nextUser) => {
              setUser(nextUser);
              setError(null);
            })
            .catch(() => setError('Oturum bilgileriniz doğrulanamadı.'))
            .finally(() => setLoading(false));
        },
        () => {
          setError('Güvenli oturum servisine şu anda ulaşılamıyor.');
          setLoading(false);
        },
      );
      return unsubscribe;
    } catch {
      setError('Portal yapılandırması eksik. Lütfen destek ekibiyle iletişime geçin.');
      setLoading(false);
      return;
    }
  }, []);

  const signInWithPortalToken = useCallback(async (customToken: string) => {
    setError(null);
    if (env.useMockApi) {
      const nextUser = mockUser() ?? {
        uid: 'shopify_7845129630',
        email: 'deniz@example.com',
        displayName: 'Deniz Yılmaz',
        isAdmin: false,
      };
      setUser(nextUser);
      return nextUser;
    }
    const credential = await signInWithCustomToken(getFirebaseAuth(), customToken);
    const nextUser = await toPortalUser(credential.user, true);
    setUser(nextUser);
    return nextUser;
  }, []);

  const refreshClaims = useCallback(async () => {
    if (env.useMockApi) {
      setUser(mockUser());
      return;
    }
    const firebaseUser = getFirebaseAuth().currentUser;
    if (!firebaseUser) {
      setUser(null);
      return;
    }
    setUser(await toPortalUser(firebaseUser, true));
  }, []);

  const signInAdminWithEmail = useCallback(async (email: string, password: string) => {
    setError(null);
    if (env.useMockApi) {
      const nextUser = mockUser();
      if (!nextUser?.isAdmin) throw new Error('Mock yönetici rolü etkin değil.');
      setUser(nextUser);
      return nextUser;
    }
    const credential = await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
    const nextUser = await requireAdminUser(credential.user);
    setUser(nextUser);
    return nextUser;
  }, []);

  const signInAdminWithGoogle = useCallback(async () => {
    setError(null);
    if (env.useMockApi) {
      const nextUser = mockUser();
      if (!nextUser?.isAdmin) throw new Error('Mock yönetici rolü etkin değil.');
      setUser(nextUser);
      return nextUser;
    }
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const credential = await signInWithPopup(getFirebaseAuth(), provider);
    const nextUser = await requireAdminUser(credential.user);
    setUser(nextUser);
    return nextUser;
  }, []);

  const signOut = useCallback(async () => {
    if (env.useMockApi) {
      setUser(null);
      return;
    }
    await firebaseSignOut(getFirebaseAuth());
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      signInWithPortalToken,
      signInAdminWithEmail,
      signInAdminWithGoogle,
      refreshClaims,
      signOut,
    }),
    [
      error,
      loading,
      refreshClaims,
      signInAdminWithEmail,
      signInAdminWithGoogle,
      signInWithPortalToken,
      signOut,
      user,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth yalnızca AuthProvider içinde kullanılabilir.');
  return context;
}
